import ky, {
	HTTPError,
	type KyRequest,
	type KyResponse,
	TimeoutError,
} from 'ky'

import type {
	DocumentationValidationErrorResponse,
	FieldErrors,
	GeneralErrors,
	ValidationErrorResponse,
} from '@/types/api.types.ts'
import { env } from '@/utilities/env.ts'
import { uniqueId } from '@/utilities/uniqueId.ts'

export type AuthenticationStatusChangeDetail =
	| 'authenticated'
	| 'unauthenticated'

export class ApiError extends Error {
	name = 'ApiError'
	data: ValidationErrorResponse & DocumentationValidationErrorResponse
	request: KyRequest
	response: KyResponse
	fields: FieldErrors
	errors: GeneralErrors

	constructor(error: HTTPError, data: ValidationErrorResponse) {
		super(error.message, error.cause ? { cause: error.cause } : undefined)

		this.data = data
		this.fields = transformFields(this.data.fields)
		this.errors = this.data.errors ?? []
		this.request = error.request
		this.response = error.response
	}

	toJSON() {
		return {
			name: this.name,
			message: this.message,
		}
	}

	get code() {
		return this.data.code
	}
}

/**
 * Transforms the shape of fields coming from API validation errors into one that can readily be used in our forms to display validation errors and distribute content attributes appropriately. For this purpose, we prepare things like “aria-invalid” and “aria-errormessage” attribute values which allows the template code to be much more terse.
 */
function transformFields(
	fields: ValidationErrorResponse['fields'] = {},
): FieldErrors {
	return Object.entries(fields).reduce<FieldErrors>(
		(fields, [field, errors]) => {
			// Normalize field name for compatibility with localization system.
			const name = field.replaceAll('.', '_')
			const id = `${name}-${uniqueId()}`

			fields[name] = {
				elAttrs: { 'aria-errormessage': id, 'aria-invalid': 'true' },
				errorAttrs: { id, errors },
			}

			return fields
		},
		{},
	)
}

export interface ErrorResult {
	status: 'error'
	error: ApiError
}

export interface OkResult<DataType> {
	status: 'ok'
	data: DataType
	response: KyResponse
}

export type RequestResult<DataType> = ErrorResult | OkResult<DataType>

/** 60 seconds */
export const API_TIMEOUT = 60 * 1000

const readingOperations = new Map<string, Promise<KyResponse>>()

class Api {
	#baseUrl
	#api

	constructor(baseUrl: string) {
		if (baseUrl.endsWith('/')) {
			throw new Error(
				`The API client's baseUrl (${baseUrl}) must not end in a slash!`,
			)
		}

		this.#baseUrl = baseUrl
		this.#api = ky.create({
			prefixUrl: this.#baseUrl,
			credentials: 'include',
			retry: {
				methods: ['get', 'head', 'delete'],
			},
			timeout: API_TIMEOUT,
			hooks: {
				afterResponse: [
					async (request, options, response) => {
						if (response.status !== 401) {
							return
						}

						this.#setUnauthenticatedStatus()
						return
					},
				],
			},
		})
	}

	get baseUrl() {
		return this.#baseUrl
	}

	/**
	 * Set a piece of global state that'll prompt them to re-authenticate if the user doesn't have valid authentication credentials (either through expiration or revocation).
	 */
	#setUnauthenticatedStatus() {
		const detail: AuthenticationStatusChangeDetail = 'unauthenticated'
		window.dispatchEvent(new CustomEvent('ts:authenticationChange', { detail }))
	}

	request = async <DataType = unknown>(
		path: string,
		options: Parameters<typeof ky>[1] = {},
	): Promise<RequestResult<DataType>> => {
		if (path.startsWith('/')) {
			throw new Error(`API paths (${path}) must not start with a slash!`)
		}

		options.method = options.method ? options.method.toUpperCase() : 'GET'
		const queryString = options.searchParams
			? options.searchParams.toString()
			: ''

		const operation =
			`${this.#baseUrl}:${options.method}:${path}` +
			(queryString ? `?${queryString}` : '')

		let response: KyResponse
		let error: HTTPError | undefined

		try {
			// Parallel reading requests using the same method and URL are de-duplicated. For this purpose, we first check if there's already a reading operation with the same method and URL in progress.
			let promise: Promise<KyResponse>
			const readingOperation = readingOperations.get(operation)

			if (readingOperation === undefined) {
				// If no other identical request is in progress, yet, make a network request …
				promise = this.#api(path, options)
				if (['GET', 'HEAD'].includes(options.method)) {
					// … and store the promise resolving to a `Response` in the map so the other identical calls can use it.
					readingOperations.set(operation, promise)
				}
			} else {
				// If a request is already in progress, avoid the network request and use the response from the map instead.
				promise = readingOperation
			}

			response = await promise
		} catch (rawError) {
			// Handle client-side timeout error
			if (rawError instanceof TimeoutError) {
				const data: ValidationErrorResponse = { code: 'request_timeout' }
				// TODO: Find a better way to handle client timeout errors.
				return {
					status: 'error',
					error: new ApiError(rawError as HTTPError, data),
				}
			}

			// Any non-response errors (i.e. network errors) shouldn't be handled here.
			if (!(rawError instanceof HTTPError)) {
				throw rawError
			}

			if (rawError.response.status >= 500) {
				console.error(rawError)
			}

			error = rawError
			response = rawError.response
		} finally {
			// Clean up in-progress reading operations.
			readingOperations.delete(operation)
		}

		const isJson = Boolean(
			response.headers.get('content-type')?.startsWith('application/json'),
		)

		const isEmpty =
			response.status === 204 ||
			(await response.clone().arrayBuffer()).byteLength === 0

		if (error) {
			let data: ValidationErrorResponse

			if (isJson) {
				data = (await response.clone().json()) as ValidationErrorResponse
			} else {
				const codes: Record<KyResponse['status'], ValidationErrorResponse> = {
					408: { code: 'request_timeout' },
					413: { code: 'content_too_large' },
					502: { code: 'bad_gateway' },
					503: { code: 'service_unavailable' },
					504: { code: 'gateway_timeout' },
				}

				data = codes[response.status] ?? { code: 'unknown' }
			}

			return { status: 'error', error: new ApiError(error, data) }
		} else {
			let data: DataType

			if (isJson && !isEmpty) {
				data = (await response.clone().json()) as DataType
			} else if (!isEmpty) {
				data = (await response.clone().arrayBuffer()) as DataType
			} else {
				data = undefined as DataType
			}

			return { status: 'ok', data, response }
		}
	}
}

// Prefix the browser origin to API base URLs without one. In the local development environment, we only specify `/api` as the API base URL so the development server can run on an arbitrary hostname and port which we would otherwise have to hard-code.
const apiBaseUrl =
	(env.API_BASE_URL.startsWith('/') ? window.location.origin : '') +
	env.API_BASE_URL
export const api = new Api(apiBaseUrl)

export type RequestType<DataType> = typeof api.request<DataType>
